import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { esObligatorio, esVisible, leer, validar, zRespuesta, type Campo, type EsquemaFormulario, type Hallazgo } from '@rumbo/form'
import { api } from '@/servicios/api'
import { Boton, Espera, MensajeError, Tarjeta } from '@/componentes/ui'
import { LockupMPPT } from '@/marca/LockupMPPT'
import { CampoFormulario } from './CampoFormulario'
import { GeoCascada, type ValorGeo } from './GeoCascada'
import { SelectorOrigen, type ValorOrigen } from './SelectorOrigen'
import { TramosEditor, type TramoUI } from './TramosEditor'
import { encolar, listarCola, quitarDeCola } from '@/servicios/cola'

interface InfoInstitucion {
  id: string
  nombre: string
  codParroquia: string
  parroquia: string
  municipio: string
  estado: string
}
interface InfoEnlace {
  abierto: boolean
  motivo: string | null
  mensaje: string | null
  campana: { id: string; formularioVersion: string }
  institucion: InfoInstitucion
  esquema: EsquemaFormulario | null
  restantes: number
}

type Datos = Record<string, unknown>

function escribir(datos: Datos, ruta: string, valor: unknown): Datos {
  const partes = ruta.split('.')
  const raiz: Datos = { ...datos }
  let cursor: Datos = raiz
  for (let i = 0; i < partes.length - 1; i++) {
    const k = partes[i]!
    cursor[k] = { ...((cursor[k] as Datos) ?? {}) }
    cursor = cursor[k] as Datos
  }
  cursor[partes.at(-1)!] = valor
  return raiz
}

// crypto.randomUUID solo existe en contextos seguros (HTTPS/localhost); por
// HTTP plano se genera un UUID v4 con getRandomValues, que sí está disponible.
const uuid = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = (b[6]! & 0x0f) | 0x40
  b[8] = (b[8]! & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

// En el modo QR la institución la fija el enlace: institucionId y su ubicación
// (geo_cascada) no se piden — se muestran como dato fijo (ver PASO estudiante).
const CAMPOS_OCULTOS_QR = new Set(['idOrganismo', 'institucionId', 'institucionUbicacion'])

export function PaginaEncuesta() {
  const { token = '' } = useParams()
  const [info, setInfo] = useState<InfoEnlace | 'cargando' | 'error'>('cargando')
  const [paso, setPaso] = useState(0)
  const [datos, setDatos] = useState<Datos>({})
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [enviando, setEnviando] = useState(false)
  const [folio, setFolio] = useState<string | null>(null)
  const [pendienteOffline, setPendienteOffline] = useState(false)
  const idRespuesta = useRef(uuid())
  const inicioRef = useRef(new Date().toISOString())

  useEffect(() => {
    api<InfoEnlace>(`/publico/enlaces/${token}`)
      .then(setInfo)
      .catch(() => setInfo('error'))
  }, [token])

  // Reintenta lo que haya quedado en la cola al montar y al recuperar señal
  // (p. ej. se cerró la app sin conexión, o se abrió el enlace en modo avión).
  useEffect(() => {
    const drenar = () => {
      listarCola().then((cola) => {
        for (const item of cola) {
          if (item.estado === 'pendiente' && item.token === token) {
            api(`/publico/enlaces/${item.token}/respuestas`, { metodo: 'POST', cuerpo: item.datos })
              .then(() => quitarDeCola(item.id))
              .catch(() => {})
          }
        }
      })
    }
    drenar()
    window.addEventListener('online', drenar)
    return () => window.removeEventListener('online', drenar)
  }, [token])

  // Todos los hooks van ANTES de cualquier retorno anticipado (reglas de los Hooks):
  // el número de hooks llamados no puede cambiar entre el estado "cargando" y el cargado.
  const infoLista = typeof info === 'object' ? info : null
  const esquema = infoLista?.esquema ?? null
  const pasos = esquema?.pasos ?? []
  const enPaso = pasos[paso]
  const camposVisibles = useMemo(
    () => (enPaso ? enPaso.campos.filter((c) => !CAMPOS_OCULTOS_QR.has(c.id) && esVisible(c, datos, 'autoaplicada_qr')) : []),
    [enPaso, datos],
  )
  const esUltimo = pasos.length > 0 && paso === pasos.length - 1
  const esPrimero = paso === 0

  const armarRespuesta = useCallback((): unknown => {
    if (!infoLista || !esquema) return null
    const geo = (datos.geo as ValorGeo) ?? {}
    const origen = (datos.origen as ValorOrigen) ?? {}
    const tramosIda = ((datos.tramos as Datos)?.ida as TramoUI[]) ?? []
    const tramosRegreso = ((datos.tramos as Datos)?.regreso as TramoUI[]) ?? []
    return {
      id: idRespuesta.current,
      campanaId: infoLista.campana.id,
      formularioVersion: infoLista.campana.formularioVersion,
      modoCaptura: 'autoaplicada_qr',
      informante: datos.informante,
      parentesco: datos.parentesco,
      consentimiento: { aceptado: true, version: esquema.consentimiento.version, aceptadoEn: new Date().toISOString() },
      asentimiento: datos.asentimiento,
      nombre: datos.nombre || undefined,
      edad: datos.edad,
      nivel: datos.nivel,
      nivelOtro: datos.nivelOtro,
      grado: datos.grado,
      institucionId: infoLista.institucion.id,
      lugarInicio: datos.lugarInicio,
      lugarInicioOtro: datos.lugarInicioOtro,
      origen: {
        codParroquia: geo.codParroquia ?? infoLista.institucion.codParroquia,
        localidad: (datos.origenLocalidad as string) || infoLista.institucion.municipio,
        sectorId: origen.sectorId,
        sectorTexto: origen.sectorTexto,
        celda: origen.celda,
        punto: origen.punto,
        metodo: origen.metodo ?? 'lista',
      },
      diasSemana: datos.diasSemana,
      horaSalidaIda: datos.horaSalidaIda,
      horaLlegadaIda: datos.horaLlegadaIda,
      modoIda: datos.modoIda,
      modoIdaOtro: datos.modoIdaOtro,
      regresoIgualIda: datos.regresoIgualIda,
      horaSalidaRegreso: datos.horaSalidaRegreso,
      horaLlegadaRegreso: datos.horaLlegadaRegreso,
      modoRegreso: datos.regresoIgualIda ? datos.modoIda : datos.modoRegreso,
      modoRegresoOtro: datos.modoRegresoOtro,
      tramos: [
        ...tramosIda.map((t) => ({ ...t, sentido: 'ida' as const })),
        ...(datos.regresoIgualIda ? [] : tramosRegreso.map((t) => ({ ...t, sentido: 'regreso' as const }))),
      ],
      gastoIda: datos.gastoIda,
      gastoRegreso: datos.gastoRegreso,
      tarifaEstudiantil: datos.tarifaEstudiantil,
      declarados: datos.declarados ?? {},
      dificultadPrincipal: datos.dificultadPrincipal,
      dificultadOtro: datos.dificultadOtro,
      dificultadesOtras: datos.dificultadesOtras ?? [],
      capturadoEn: inicioRef.current,
    }
  }, [infoLista, esquema, datos])

  const hallazgos: Hallazgo[] = useMemo(() => {
    if (!esUltimo) return []
    const payload = armarRespuesta()
    if (!payload) return []
    try {
      return validar(payload as never)
    } catch {
      return []
    }
  }, [esUltimo, armarRespuesta])

  const validarPaso = useCallback((): boolean => {
    if (!enPaso) return false
    const nuevosErrores: Record<string, string> = {}
    for (const c of camposVisibles) {
      if (c.id === 'origen') continue
      const obligatorio = esObligatorio(c, datos, 'autoaplicada_qr')
      const valor = leer(datos, c.id)
      if (obligatorio && (valor === undefined || valor === '' || valor === null)) nuevosErrores[c.id] = 'Este dato es obligatorio.'
    }
    if (enPaso.id === 'origen') {
      const origen = datos.origen as ValorOrigen | undefined
      if (!origen?.sectorId && !origen?.celda && !origen?.sectorTexto) nuevosErrores.origen = 'Indique su sector, en la lista, el mapa o escribiéndolo.'
    }
    setErrores(nuevosErrores)
    return Object.keys(nuevosErrores).length === 0
  }, [enPaso, camposVisibles, datos])

  async function enviar() {
    if (!validarPaso()) return
    const payload = armarRespuesta()
    const parseo = zRespuesta.safeParse(payload)
    if (!parseo.success) {
      setErrores({ general: 'Faltan datos por completar. Revise los pasos anteriores.' })
      return
    }
    setEnviando(true)
    try {
      const r = await api<{ folio: string }>(`/publico/enlaces/${token}/respuestas`, { metodo: 'POST', cuerpo: parseo.data })
      setFolio(r.folio)
    } catch {
      // Sin conexión (u otro error de red): se guarda localmente y se reintenta solo.
      await encolar({ id: idRespuesta.current, token, datos: parseo.data, estado: 'pendiente', intentos: 0, guardadoEn: new Date().toISOString() })
      setPendienteOffline(true)
      setFolio('PENDIENTE')
    } finally {
      setEnviando(false)
    }
  }

  if (info === 'cargando') {
    return (
      <PantallaCentrada>
        <Espera etiqueta="Cargando la encuesta…" />
      </PantallaCentrada>
    )
  }
  if (info === 'error') return <PantallaMensaje titulo="No pudimos abrir la encuesta" texto="Verifique su conexión o el código QR e intente de nuevo." />
  if (!info.abierto) return <PantallaMensaje titulo="Esta encuesta no está disponible" texto={info.mensaje ?? 'Intente más tarde.'} />
  if (folio) return <PantallaGracias folio={folio} offline={pendienteOffline} />
  if (!enPaso) return null

  return (
    <div className="mx-auto min-h-screen max-w-2xl px-4 pb-28 pt-6">
      <header className="mb-6 flex items-center justify-between">
        <LockupMPPT ancho={140} />
      </header>

      <Tarjeta className="p-5 sm:p-7">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-texto-tenue">
          {info.institucion.nombre} · Paso {paso + 1} de {pasos.length}
        </p>
        <h1 className="mb-1 text-2xl font-bold text-azul">{enPaso.titulo}</h1>
        {enPaso.descripcion && <p className="mb-5 text-sm text-texto-suave">{enPaso.descripcion}</p>}

        <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-superficie-alt">
          <div className="h-full rounded-full bg-oro transition-[width] duration-300" style={{ width: `${((paso + 1) / pasos.length) * 100}%` }} />
        </div>

        {paso === 0 && <PasoConsentimiento esquema={esquema!} datos={datos} setDatos={setDatos} errores={errores} />}

        <div className="space-y-6">
          {paso !== 0 && enPaso.id === 'estudiante' && (
            <div className="rounded-[var(--radius-rumbo)] bg-superficie-alt p-3.5 text-sm">
              <span className="text-texto-suave">Institución educativa: </span>
              <span className="font-medium text-texto">{info.institucion.nombre}</span>
              <span className="block text-texto-tenue">
                {info.institucion.parroquia}, {info.institucion.municipio}, {info.institucion.estado}
              </span>
            </div>
          )}
          {paso !== 0 && camposVisibles.map((c) => <CampoRenderer key={c.id} campo={c} datos={datos} setDatos={setDatos} error={errores[c.id]} />)}
        </div>

        {esUltimo && hallazgos.some((h) => h.severidad === 'advertencia') && (
          <div className="mt-6 rounded-[var(--radius-rumbo)] bg-advertencia-fondo p-4 text-sm text-advertencia">
            <p className="mb-1 font-medium">Antes de enviar, revise:</p>
            <ul className="list-inside list-disc space-y-0.5">
              {hallazgos
                .filter((h) => h.severidad === 'advertencia')
                .map((h, i) => (
                  <li key={i}>{h.mensaje}</li>
                ))}
            </ul>
          </div>
        )}
        {errores.general && <MensajeError>{errores.general}</MensajeError>}
      </Tarjeta>

      <div className="fixed inset-x-0 bottom-0 border-t border-borde bg-superficie/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-2xl gap-3">
          {!esPrimero && (
            <Boton variante="fantasma" onClick={() => setPaso((p) => p - 1)} className="flex-1">
              Atrás
            </Boton>
          )}
          <Boton
            className="flex-1"
            cargando={enviando}
            onClick={() => {
              if (paso === 0 && !(datos.consentimiento as boolean)) return setErrores({ consentimiento: 'Debe aceptar para continuar.' })
              if (!validarPaso()) return
              if (esUltimo) void enviar()
              else setPaso((p) => p + 1)
            }}
          >
            {esUltimo ? 'Enviar respuestas' : 'Continuar'}
          </Boton>
        </div>
      </div>
    </div>
  )
}

function CampoRenderer({ campo, datos, setDatos, error }: { campo: Campo; datos: Datos; setDatos: (fn: (d: Datos) => Datos) => void; error?: string }) {
  const cambiar = (v: unknown) => setDatos((d) => escribir(d, campo.id, v))

  if (campo.id === 'origen') {
    const geo = (datos.geo as ValorGeo) ?? {}
    return (
      <div className="space-y-4">
        <div>
          <p className="mb-1.5 text-[15px] font-medium text-texto">Estado, municipio y parroquia de origen</p>
          <GeoCascada valor={geo} onCambio={(v) => setDatos((d) => escribir(d, 'geo', v))} />
        </div>
        <div>
          <p className="mb-1.5 text-[15px] font-medium text-texto">
            {campo.etiqueta}
            <span className="ml-1 text-rojo">*</span>
          </p>
          {campo.ayuda && <p className="mb-2 text-sm text-texto-suave">{campo.ayuda}</p>}
          <SelectorOrigen codParroquia={geo.codParroquia ?? null} valor={datos.origen as ValorOrigen} onCambio={(v) => setDatos((d) => escribir(d, 'origen', v))} />
          <MensajeError>{error}</MensajeError>
        </div>
      </div>
    )
  }

  if (campo.id.startsWith('tramos.')) {
    const sentido = campo.id.endsWith('ida') ? 'ida' : 'regreso'
    if (sentido === 'regreso' && datos.regresoIgualIda) return null
    return (
      <div>
        <p className="mb-1.5 text-[15px] font-medium text-texto">{campo.etiqueta}</p>
        {campo.ayuda && <p className="mb-2 text-sm text-texto-suave">{campo.ayuda}</p>}
        <TramosEditor tramos={((datos.tramos as Datos)?.[sentido] as TramoUI[]) ?? []} onCambio={(t) => setDatos((d) => escribir(d, `tramos.${sentido}`, t))} />
      </div>
    )
  }

  return <CampoFormulario campo={campo} valor={leer(datos, campo.id)} onCambio={cambiar} error={error} obligatorio={esObligatorio(campo, datos, 'autoaplicada_qr')} />
}

function PasoConsentimiento({
  esquema,
  datos,
  setDatos,
  errores,
}: {
  esquema: EsquemaFormulario
  datos: Datos
  setDatos: (fn: (d: Datos) => Datos) => void
  errores: Record<string, string>
}) {
  return (
    <div className="space-y-5">
      <p className="text-sm leading-relaxed text-texto">{esquema.objetivo}</p>
      <div className="rounded-[var(--radius-rumbo)] bg-info-fondo p-4 text-sm text-info">{esquema.consentimiento.texto}</div>
      <label className="objetivo-tactil foco-visible flex cursor-pointer items-start gap-3 rounded-[var(--radius-rumbo)] border border-borde-fuerte p-4">
        <input
          type="checkbox"
          checked={Boolean(datos.consentimiento)}
          onChange={(e) => setDatos((d) => ({ ...d, consentimiento: e.target.checked }))}
          className="mt-0.5 size-5 accent-[var(--mppt-azul-marino)]"
        />
        <span className="text-sm font-medium text-texto">Acepto participar en la encuesta</span>
      </label>
      <MensajeError>{errores.consentimiento}</MensajeError>

      <div>
        <p className="mb-2 text-[15px] font-medium text-texto">¿Quién responde la encuesta?</p>
        <p className="mb-2 text-sm text-texto-suave">En educación inicial y primaria responde el representante.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            { v: 'estudiante', t: 'El estudiante' },
            { v: 'representante', t: 'Su representante (madre, padre o responsable)' },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setDatos((d) => ({ ...d, informante: o.v }))}
              className={`objetivo-tactil foco-visible rounded-[var(--radius-rumbo)] border px-3.5 py-2.5 text-left text-sm ${
                datos.informante === o.v ? 'border-azul bg-azul-suave font-medium text-azul' : 'border-borde-fuerte text-texto'
              }`}
            >
              {o.t}
            </button>
          ))}
        </div>
      </div>

      {datos.informante === 'representante' && (
        <div>
          <p className="mb-2 text-[15px] font-medium text-texto">Parentesco con el estudiante</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {['madre', 'padre', 'abuelo', 'tio', 'otro'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDatos((d) => ({ ...d, parentesco: p }))}
                className={`objetivo-tactil foco-visible rounded-[var(--radius-rumbo)] border px-2 py-2 text-sm capitalize ${
                  datos.parentesco === p ? 'border-azul bg-azul-suave font-medium text-azul' : 'border-borde-fuerte'
                }`}
              >
                {p === 'tio' ? 'Tía/o' : p === 'abuelo' ? 'Abuela/o' : p}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function PantallaCentrada({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center px-4">{children}</div>
}

function PantallaMensaje({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <PantallaCentrada>
      <Tarjeta className="max-w-md p-8 text-center">
        <LockupMPPT ancho={140} className="mx-auto mb-6" />
        <h1 className="mb-2 text-xl font-bold text-azul">{titulo}</h1>
        <p className="text-texto-suave">{texto}</p>
      </Tarjeta>
    </PantallaCentrada>
  )
}

function PantallaGracias({ folio, offline }: { folio: string; offline: boolean }) {
  return (
    <PantallaCentrada>
      <Tarjeta className="max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-exito-fondo text-exito">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="size-8" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="mb-2 text-xl font-bold text-azul">¡Gracias por responder!</h1>
        {offline ? (
          <p className="text-texto-suave">Su respuesta se guardó en este dispositivo y se enviará sola en cuanto haya señal.</p>
        ) : (
          <>
            <p className="mb-1 text-texto-suave">Su respuesta fue recibida.</p>
            <p className="font-mono text-sm text-texto-tenue">Folio {folio}</p>
          </>
        )}
      </Tarjeta>
    </PantallaCentrada>
  )
}
